import type { PiniaPlugin } from 'pinia';
import { defineStore } from 'pinia';
import API from '@/api';
import { merchantInterface } from '@/api/merchants';
import { Spark } from '@/compiler/types';
import { useCurrentUserStore } from '@/stores/currentUser';
import { useMerchantsStore } from '@/stores/merchants';

interface State extends Spark {
    selectedMerchantLocked: boolean
    // The manually locked selection a resource URL is temporarily overriding, or null when
    // no override is active. Wrapped so a locked "all merchants" (undefined) is distinct.
    lockedMerchantBeforeRoute: { merchant: merchantInterface | undefined } | null
}

const selectedMerchantStorageKey = 'selectedMerchantId';

type AppEnv = State['env'];
const validEnvs: AppEnv[] = ['local', 'production', 'demo', 'testing', 'staging'];
function getEnv(value: string | undefined): AppEnv {
    return validEnvs.find(e => e === value) ?? 'local';
}

// Used until the narrative loads (e.g. the unauthenticated register step, where
// the service-provider endpoint 404s). loadNarrative overrides this with the
// real platformName once a service provider is available. The host must be the
// apex domain or a subdomain of it — a bare substring match would wrongly brand
// an unrelated host like prepbusiness.example.com.
const getDefaultAppName = (): string => {
    const { hostname } = window.location;
    const isHostOf = (domain: string): boolean => hostname === domain || hostname.endsWith(`.${domain}`);

    if (isHostOf('conveyr.com')) return 'Conveyr';
    if (isHostOf('prepbusiness.com')) return 'PrepBusiness';

    return 'Portal';
};

// The platform's own entry hosts — where register-company, subdomain-select and the emailed
// verification links land. They deliberately have no service provider, so the narrative
// endpoint 404s there and the only visible result was an error notification on those pages.
//
// Listed exactly rather than matched on an `admin.` prefix: a service provider can point a
// custom domain at the platform, so admin.acme.com may well be a tenant that does have one.
// Skipping the load for a tenant costs it its branding and bounces its authed routes to
// login, so any host not known to be ours must fall through and ask.
const platformAdminHosts = ['admin.conveyr.com', 'admin.prepbusiness.com'];

const getSelectedMerchantIdFromStorage = (): number | null => {
    const sessionStorageId = sessionStorage.getItem(selectedMerchantStorageKey);
    if (sessionStorageId != null && sessionStorageId !== '') return Number.parseInt(sessionStorageId);

    const localStorageId = localStorage.getItem(selectedMerchantStorageKey);
    if (localStorageId != null && localStorageId !== '') return Number.parseInt(localStorageId);

    return null;
};

export const narrativeMerchantsPlugin: PiniaPlugin = ({ store }) => {
    if (store.$id !== 'narrative') return;

    const merchantsStore = useMerchantsStore();

    merchantsStore.$onAction(({ name, after }) => {
        if (name !== 'fetchMerchants') return;
        after(() => {
            const narrativeStore = useNarrativeStore();
            if (narrativeStore.merchant) {
                narrativeStore.$patch({
                    merchant: merchantsStore.merchants.find(merchant => merchant.id === narrativeStore.merchant?.id),
                });
                return;
            }
            const storedId = getSelectedMerchantIdFromStorage();
            const stored = storedId == null ? undefined : merchantsStore.merchants.find(merchant => merchant.id === storedId);
            if (stored !== undefined) {
                narrativeStore.setCurrentMerchant(stored);
                return;
            }

            if (storedId != null) {
                narrativeStore.clearCurrentMerchant();
            }

            // Narration is otherwise only ever established by an explicit pick on the account
            // selector, so a merchant user on a fresh browser or with cleared storage has none
            // and every merchant-scoped endpoint refuses them. With a single account there is
            // nothing to pick, so select it here. Staff are excluded: their list is every
            // merchant on the service provider, which says nothing about who they are viewing.
            if (useCurrentUserStore().userHasRoleOnCurrentServiceProvider()) return;
            if (merchantsStore.merchants.length !== 1) return;

            const [onlyMerchant] = merchantsStore.merchants;
            if (onlyMerchant === undefined) return;

            narrativeStore.setCurrentMerchant(onlyMerchant);
        });
    });
};

export const useNarrativeStore = defineStore('narrative', {
    state: (): State => ({
        env: getEnv(import.meta.env.VITE_APP_ENV),
        appName: getDefaultAppName(),
        appBaseDomain: '',
        stripeKey: import.meta.env.VITE_STRIPE_KEY ?? '',
        serviceProvider: undefined,
        merchant: undefined,
        selectedMerchantLocked: false,
        lockedMerchantBeforeRoute: null,
        impersonatingUserId: null,
        sentryPublicDsn: import.meta.env.VITE_SENTRY_PUBLIC_DSN,
        releaseTag: import.meta.env.VITE_RELEASE_TAG,
        analyticsKeys: {
            posthog: {
                apiKey: import.meta.env.VITE_POSTHOG_API_KEY ?? '',
                apiHost: import.meta.env.VITE_POSTHOG_API_HOST ?? '',
            },
        },
        chargebeeSite: import.meta.env.VITE_CHARGEBEE_SITE ?? '',
        featurebaseUserIdHash: null,
        featurebaseServiceProviderIdHash: null,
        featurebaseOrganization: import.meta.env.VITE_FEATUREBASE_ORGANIZATION ?? null,
    }),
    getters: {
        currentServiceProvider: state => state.serviceProvider,
        currentMerchant: state => state.merchant,
        currentAppName: state => state.appName,
        currentAppBaseDomain: state => state.appBaseDomain,
        getSelectedMerchantLocked: state => state.selectedMerchantLocked,
    },
    actions: {
        setCurrentMerchant(merchant: merchantInterface): void {
            if (this.selectedMerchantLocked) {
                return;
            }

            if (this.merchant?.id === merchant.id) {
                return;
            }

            sessionStorage.setItem(selectedMerchantStorageKey, merchant.id.toString());
            localStorage.setItem(selectedMerchantStorageKey, merchant.id.toString());

            this.merchant = merchant;
        },
        // Bypasses the manual-lock guard above: a resource only loads under its own
        // merchant, so its URL must win even over a locked selector. Under a lock the
        // override is temporary: the locked selection is kept (and left in storage) so
        // restoreLockedMerchant can bring it back once the user leaves the resource.
        setCurrentMerchantFromRoute(merchant: merchantInterface): void {
            if (this.selectedMerchantLocked && this.lockedMerchantBeforeRoute === null) {
                this.lockedMerchantBeforeRoute = { merchant: this.merchant };
            }

            if (this.merchant?.id === merchant.id) {
                return;
            }

            if (!this.selectedMerchantLocked) {
                sessionStorage.setItem(selectedMerchantStorageKey, merchant.id.toString());
                localStorage.setItem(selectedMerchantStorageKey, merchant.id.toString());
            }

            this.merchant = merchant;
        },
        restoreLockedMerchant(): void {
            if (this.lockedMerchantBeforeRoute === null) {
                return;
            }

            this.merchant = this.lockedMerchantBeforeRoute.merchant;
            this.lockedMerchantBeforeRoute = null;
        },
        clearCurrentMerchant(): void {
            sessionStorage.removeItem(selectedMerchantStorageKey);
            localStorage.removeItem(selectedMerchantStorageKey);

            this.merchant = undefined;
        },
        setServiceProviderLogo(value: string): void {
            if (!this.serviceProvider) return;
            this.serviceProvider.logoFile = value;
        },
        setServiceProviderLogoBackground(value: string): void {
            if (!this.serviceProvider) return;
            this.serviceProvider.logoBackground = value;
        },
        setImpersonatedUserId(value: number): void {
            this.impersonatingUserId = value;
        },
        clearImpersonatedUserId(): void {
            this.impersonatingUserId = null;
        },
        toggleSelectedMerchantLocked(): void {
            this.selectedMerchantLocked = !this.selectedMerchantLocked;
            this.lockedMerchantBeforeRoute = null;
        },
        async refreshServiceProvider(): Promise<void> {
            this.serviceProvider = await API.serviceProviders.fetchCurrentServiceProvider();
        },
        async loadNarrative(): Promise<void> {
            if (this.serviceProvider) {
                return;
            }

            // Leaves serviceProvider undefined, which callers already treat as "no service
            // provider for this context" — guest pages keep the hostname-based branding and
            // the authed guard sends the user to login, same as a failed load.
            if (platformAdminHosts.includes(window.location.hostname)) {
                return;
            }

            this.serviceProvider = await API.serviceProviders.fetchCurrentServiceProvider();

            this.appName = this.serviceProvider.platformName;
            this.appBaseDomain = this.serviceProvider.platformDomain;
        },
    },
});
